import { ActionBar, Button, ModalV2 } from '@components'
import { PermissionSet, useBusinessContext, useFeature, usePermission } from '@context'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaPlus, FaShare } from 'react-icons/fa'
import { EInvoicingShareModalContent } from '@pages/SettingsPage/sections/EInvoicingSettings/EInvoicingShareModalContent'
import { CreateInvoiceModal } from './CreateInvoiceModal'
import { useQuery } from '@tanstack/react-query'
import { fetchBusiness } from '@root/query'
import { IFilter } from '@root/components/ActionBar/FilterBar/FilterSelection'
import { InvoiceImportWizard } from '@containers/CSVImportWizard/InvoiceImportWizard.tsx'
import { FaFileCsv, FaSackDollar } from 'react-icons/fa6'
import { FaEnvelope } from 'react-icons/fa6'
import { PurchaseInvoiceImport } from './PurchaseInvoiceImport'
import { useHistory } from 'react-router-dom'
import { isBusinessExcludedFromNojaAd } from '@root/utils/isNojaAdActive'
import { usePurchaseInvoiceImportTracker } from './hooks/usePurchaseInvoiceImportTracker'
import { setTagsInFilter } from '@root/utils/filterHelpers'
import { TagSelector } from '@root/containers/TagSelector/TagSelector'
import { useOpenCreateFromQuery } from '@hooks'

interface Props {
  onSearch: (value: string) => void
  onFilter: (filters: any) => void
  activeFilters: IFilter[]
}

export const InvoicingActionBar: React.FC<Props> = ({ onSearch, onFilter, activeFilters }) => {
  const [showCreate, setShowCreate] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const history = useHistory()
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const { startTracking } = usePurchaseInvoiceImportTracker()
  const { data: business } = useQuery({
    queryKey: [slug],
    queryFn: () => fetchBusiness({ slug })
  })
  const hasEditPermission = usePermission(PermissionSet.INVOICING_EDIT)
  const { enabled: kraviaEnabled } = useFeature('kravia')
  const { enabled: nojaEnabled } = useFeature('noja')
  const [isEmailInvoiceImportModalOpen, setIsEmailInvoiceImportModalOpen] = useState(false)
  const [showShareEInvoicing, setShowShareEInvoicing] = useState(false)
  const isExcludedFromNojaAd = isBusinessExcludedFromNojaAd(business)

  useOpenCreateFromQuery(() => setShowCreate(true), hasEditPermission)

  const handlePurchaseInvoiceEmailImportModalClose = () => {
    setIsEmailInvoiceImportModalOpen(false)
  }

  const filterButtons = [
    {
      id: 'sent-invoices',
      name: t('invoicing.filters.sentInvoices'),
      filter: { only_sales: true }
    },
    {
      id: 'received-invoices',
      name: t('invoicing.filters.receivedInvoices'),
      filter: { only_purchases: true }
    },
    {
      id: 'is-open',
      name: t('invoicing.filters.isOpen'),
      filter: { is_open: true }
    },
    {
      id: 'is-over-due',
      name: t('invoicing.filters.isOverDue'),
      filter: { is_over_due: true }
    },
    ...(kraviaEnabled
      ? [
          {
            id: 'in-debt-collection',
            name: t('invoicing.filters.inDebtCollection'),
            filter: { in_debt_collection: true }
          }
        ]
      : [])
  ]

  const filterInputs = [
    ...(business?.has_tags
      ? [
          <TagSelector
            label={t('components.tagFilterSelector.label')}
            key={'tags_filter_selector'}
            value={activeFilters.filter(value => 'tags' in value).map(value => value.tags)[0]}
            onChange={(tagIds: number[]) => {
              const updatedFilters = setTagsInFilter(activeFilters, tagIds)
              onFilter(updatedFilters)
            }}
          />
        ]
      : [])
  ]

  const primaryButtons = [
    <Button
      icon={<FaPlus />}
      onClick={() => setShowCreate(true)}
      key="new-invoice-button"
      data-test="new-invoice-button"
    >
      {t('invoicing.actions.create')}
    </Button>,
    <Button
      key="csv-import"
      icon={<FaFileCsv />}
      onClick={() => setShowImport(true)}
      disabled={!business?.can_invoice}
    >
      {t('invoicing.import.button')}
    </Button>,
    <Button
      key="purchase-invoice-import-button"
      icon={<FaEnvelope />}
      onClick={() => {
        setIsEmailInvoiceImportModalOpen(true)
      }}
    >
      {t('invoicing.actions.importPurchaseInvoice')}
    </Button>,

    ...(nojaEnabled && !isExcludedFromNojaAd
      ? [
          <Button key="noja-ad-button" icon={<FaSackDollar />} onClick={() => history.push('noja')}>
            {t('invoicing.actions.nojaAd')}
          </Button>
        ]
      : []),
    ...(business?.einvoicing_enabled
      ? [
          <Button
            key="share-einvoicing-details"
            icon={<FaShare />}
            onClick={() => setShowShareEInvoicing(true)}
          >
            {t('invoicing.actions.shareEInvoicingDetails')}
          </Button>
        ]
      : [])
  ]

  return (
    <>
      <ActionBar
        searchPlaceholder={t('invoicing.actions.search')}
        onSearch={search => onSearch(search)}
        onFilter={filters => onFilter(filters)}
        filterButtons={filterButtons}
        filterInputs={filterInputs}
        activeFilters={activeFilters}
        primaryButtons={hasEditPermission && primaryButtons}
      />

      {showCreate && (
        <CreateInvoiceModal
          onClose={() => setShowCreate(false)}
          onSubmit={() => setShowCreate(false)}
        />
      )}

      <ModalV2
        isVisible={showImport}
        header={t('invoicing.import.title')}
        handleOnClose={() => setShowImport(false)}
        width={800}
        height={900}
      >
        <InvoiceImportWizard onComplete={() => setShowImport(false)} />
      </ModalV2>

      <ModalV2
        isVisible={isEmailInvoiceImportModalOpen}
        header={t('invoicing.purchaseInvoiceImport.modalTitle')}
        handleOnClose={handlePurchaseInvoiceEmailImportModalClose}
        footer={
          <>
            <span />
            <Button onClick={handlePurchaseInvoiceEmailImportModalClose}>
              {t('general.close')}
            </Button>
          </>
        }
        width={760}
        autoHeight
      >
        <PurchaseInvoiceImport
          onImportStarted={({ jobId, purchaseInvoiceId }) => {
            startTracking({ jobId, purchaseInvoiceId })
            setIsEmailInvoiceImportModalOpen(false)
          }}
        />
      </ModalV2>

      <ModalV2
        isVisible={showShareEInvoicing}
        header={t('invoicing.shareEInvoicing.modalTitle')}
        handleOnClose={() => setShowShareEInvoicing(false)}
        width={560}
        autoHeight
      >
        {business && <EInvoicingShareModalContent business={business} />}
      </ModalV2>
    </>
  )
}
